import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';

export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <main className="py-16">
        <Container size="md">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: March 19, 2026
                </p>
              </div>

              <div className="prose prose-lg max-w-none">

                {/* 1. Introduction */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
                  <p className="text-muted-foreground mb-4">
                    Cascady is operated by Zero Point Studio d.o.o. ("we," "our," or "us"), a company registered in Zagreb, Croatia.
                    We are committed to protecting your privacy and ensuring the security of your personal information in compliance with the
                    General Data Protection Regulation (GDPR) and other applicable data protection laws.
                  </p>
                  <p className="text-muted-foreground">
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
                    Cascady — our visual AI creative workflow platform for generating images and video using artificial intelligence.
                    By using our Service, you agree to the collection and use of information in accordance with this policy.
                  </p>
                </section>

                {/* 2. Data Controller */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Data Controller Information</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Company:</strong> Zero Point Studio d.o.o.</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground mb-2"><strong>Phone:</strong> +385 91 722 8780</p>
                    <p className="text-muted-foreground mb-2"><strong>Director:</strong> Andrej Šimunaj</p>
                    <p className="text-muted-foreground"><strong>Registration Date:</strong> April 24, 2024</p>
                  </div>
                </section>

                {/* 3. Information We Collect */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. Information We Collect</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.1 Personal Information</h3>
                  <p className="text-muted-foreground mb-4">When you register for an account, we collect:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Email address</li>
                    <li>Name (optional)</li>
                    <li>Password (stored as an encrypted hash)</li>
                    <li>Account preferences and settings</li>
                    <li>Organization name and details</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.2 Usage Information</h3>
                  <p className="text-muted-foreground mb-4">We automatically collect certain information when you use our Service:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>IP address, browser type, and operating system</li>
                    <li>Feature interactions and usage patterns</li>
                    <li>AI model usage, generation parameters, and credit consumption</li>
                    <li>Canvas activity and workflow data</li>
                    <li>Performance metrics and error reports</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.3 Content Information</h3>
                  <p className="text-muted-foreground mb-4">
                    Images, reference files, prompts, and other content you upload or create on Cascady are stored securely and
                    used solely for providing our Service. Your content may be transmitted to third-party AI services for the purpose
                    of generating images or video as part of your creative workflows.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.4 Payment Information</h3>
                  <p className="text-muted-foreground">
                    Payment processing is handled by Stripe, Inc. We store your Stripe customer ID, subscription ID, invoice identifiers,
                    and credit transaction records. We do <strong>not</strong> store your full credit card number, CVV, or other sensitive
                    payment credentials — these are collected and processed directly by Stripe. Please review{' '}
                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">Stripe's Privacy Policy</a> for details on how they handle your payment data.
                  </p>
                </section>

                {/* 4. How We Use Your Information */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. How We Use Your Information</h2>
                  <p className="text-muted-foreground mb-4">We use your information for the following purposes:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Providing and maintaining the Cascady platform and AI generation services</li>
                    <li>Authenticating your identity and managing account access</li>
                    <li>Processing payments, managing subscriptions, and tracking credit usage</li>
                    <li>Monitoring usage to enforce plan limits and prevent abuse</li>
                    <li>Improving platform performance, reliability, and feature development</li>
                    <li>Ensuring security and detecting fraudulent activity</li>
                    <li>Sending important service communications (billing, security, policy changes)</li>
                    <li>Complying with legal obligations</li>
                  </ul>
                </section>

                {/* 5. GDPR Legal Basis */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Legal Basis for Processing (GDPR)</h2>
                  <p className="text-muted-foreground mb-4">We process your personal data under the following legal bases:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Contract Performance:</strong> Processing necessary to provide the services you have subscribed to</li>
                    <li><strong>Legitimate Interests:</strong> Processing for service improvement, security, and fraud prevention</li>
                    <li><strong>Legal Obligation:</strong> Processing required by applicable laws (tax records, regulatory compliance)</li>
                    <li><strong>Consent:</strong> Where you have explicitly opted in (e.g., marketing communications)</li>
                  </ul>
                </section>

                {/* 6. Third-Party Services */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. Third-Party Services</h2>
                  <p className="text-muted-foreground mb-4">
                    We do not sell, trade, or rent your personal information. We share data with the following categories of service providers
                    only as necessary to operate the Service:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Stripe</strong> — Payment processing, subscription management, and invoicing</li>
                    <li><strong>AI Model Providers (fal.ai, Google Gemini, and others)</strong> — Your prompts and reference images are sent to these services to generate AI content. Generated outputs are returned to your account.</li>
                    <li><strong>Cloudflare R2</strong> — Cloud storage for your uploaded and generated images</li>
                    <li><strong>Brevo</strong> — Transactional email delivery (account notifications, billing alerts)</li>
                    <li><strong>Sentry</strong> — Error monitoring and crash reporting (no personal content transmitted)</li>
                    <li><strong>Google Analytics</strong> — Anonymous usage analytics</li>
                  </ul>
                  <p className="text-muted-foreground">
                    We may also disclose your information when required by law, to protect our rights, or in connection with a merger, acquisition, or sale of assets.
                  </p>
                </section>

                {/* 7. Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. Cookies and Tracking Technologies</h2>
                  <p className="text-muted-foreground mb-4">We use the following types of cookies:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Essential Cookies:</strong> Required for authentication, session management, and core functionality. Cannot be disabled.</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand usage patterns and improve the platform (Google Analytics).</li>
                    <li><strong>Preference Cookies:</strong> Store your settings, theme, and workspace preferences.</li>
                  </ul>
                  <p className="text-muted-foreground">
                    You can control cookie settings through your browser preferences. Disabling essential cookies may prevent the Service from functioning correctly.
                    For more details, see our <a href="/cookie-policy" className="text-primary-600 underline">Cookie Policy</a>.
                  </p>
                </section>

                {/* 8. Your Rights (GDPR) */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">8. Your Rights Under GDPR</h2>
                  <p className="text-muted-foreground mb-4">As a data subject, you have the following rights under GDPR:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Right of Access (Article 15):</strong> Request a copy of the personal data we hold about you</li>
                    <li><strong>Right to Rectification (Article 16):</strong> Request correction of inaccurate or incomplete data</li>
                    <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your personal data</li>
                    <li><strong>Right to Restrict Processing (Article 18):</strong> Request that we limit how we use your data</li>
                    <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interests</li>
                    <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you may withdraw at any time</li>
                  </ul>
                  <p className="text-muted-foreground">
                    To exercise any of these rights, please contact us at andrej@zeropointstudio.io. We will respond to your request within 30 days.
                  </p>
                </section>

                {/* 9. Data Retention */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">9. Data Retention</h2>
                  <p className="text-muted-foreground mb-4">We retain your data according to the following schedule:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Active accounts:</strong> Data retained for the duration of your subscription</li>
                    <li><strong>Deleted accounts:</strong> Personal data deleted or anonymized within 30 days, except where retention is required by law</li>
                    <li><strong>Usage logs:</strong> Retained for up to 1 year for analytics and abuse prevention</li>
                    <li><strong>Payment records:</strong> Retained for 7 years as required for tax and regulatory compliance</li>
                    <li><strong>Generated content:</strong> Stored while your account is active; deleted upon account closure</li>
                  </ul>
                </section>

                {/* 10. Data Security */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">10. Data Security</h2>
                  <p className="text-muted-foreground mb-4">
                    We implement appropriate technical and organizational measures to protect your personal information, including:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Encryption of data in transit (HTTPS/TLS)</li>
                    <li>Encrypted password storage using industry-standard hashing</li>
                    <li>Regular security updates and monitoring</li>
                    <li>Access controls and authentication</li>
                    <li>Regular backups with encryption</li>
                    <li>Security incident monitoring and response procedures</li>
                  </ul>
                  <p className="text-muted-foreground">
                    While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure.
                  </p>
                </section>

                {/* 11. International Data Transfers */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">11. International Data Transfers</h2>
                  <p className="text-muted-foreground mb-4">
                    Your information may be transferred to and processed in countries outside the European Economic Area (EEA).
                    When we transfer data internationally, we ensure appropriate safeguards are in place, including:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Standard Contractual Clauses approved by the European Commission</li>
                    <li>Adequacy decisions where applicable</li>
                    <li>Appropriate technical and organizational safeguards compliant with GDPR requirements</li>
                  </ul>
                </section>

                {/* 12. Children's Privacy */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">12. Children's Privacy</h2>
                  <p className="text-muted-foreground">
                    Our Service is not intended for users under 18 years of age. We do not knowingly collect personal information from
                    anyone under 18. If we become aware that we have collected personal data from a minor, we will take steps to delete
                    that information promptly.
                  </p>
                </section>

                {/* 13. Changes */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">13. Changes to This Policy</h2>
                  <p className="text-muted-foreground mb-4">
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by email or by
                    posting a prominent notice on our Service, and updating the "Last updated" date at the top of this page.
                  </p>
                  <p className="text-muted-foreground">
                    Your continued use of the Service after any changes constitutes acceptance of the updated policy.
                  </p>
                </section>

                {/* 14. Contact */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">14. Contact Us</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have any questions about this Privacy Policy, your data, or wish to exercise your GDPR rights, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Company:</strong> Zero Point Studio d.o.o.</p>
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                    <p className="text-muted-foreground"><strong>Phone:</strong> +385 91 722 8780</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
